
import React, { useEffect, useState } from 'react'
import { Row, Col, Alert, Form, FormCheck, Badge, CloseButton } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import Card from '../../../components/Card'

import { TerminalItem } from '../../../features/terminals';
import { getTerminals, getTxCount, getPropertyInfo } from '../../../services/api/terminal';
import { ApiError, Terminal } from '../../../services/openapi';

import { useKeycloak } from '@react-keycloak/web'

import SearchInput from '../../../components/SearchInput';
import ListPagination from '../../../components/ListPagination';
import RefreshPull from '../../../components/RefreshPull';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { peekPendingSelectedId, clearPendingSelectedId } from '../../../hooks/pendingSelection';
import { ApiResult } from '../../../services/openapi/core/ApiResult';
import CsvExport from '../../../components/exports/CsvExport';
import { useTranslation } from 'react-i18next'; 
import { NavContext } from '../../../components/dto/NavContext';
import { resetNavContext } from '../../../hooks/navContext';

const Terminals = () => {
    const [terminals, setTerminals] = useState<Terminal[]>([]);    
    const [terminalCount, setTerminalCount] = useState<string>("0");    
    const [pagingString, setPagingString] = useState<string>("0-25");        
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [tableBodyHeight, setTableBodyHeight] = useState<number>(380);    
    const [formData, setFormData] = useLocalStorage<string>("terminals", "formData", "");
    const [searchData, setSearchData] = useLocalStorage<Record<string, string>>("terminals", "searchData", {});
    const [filterData, setFilterData] = useLocalStorage<string>("terminals", "filterData", "");
    const [currentPage, setCurrentPage] = useLocalStorage<number>("terminals", "currentPage", 0);
    const [siteFilter, setSiteFilter] = useLocalStorage<number[]>("sites", "siteFilter", []);
    const [siteFilterQuery, setSiteFilterQuery] = useLocalStorage<string>("sites", "siteFilterQuery", "");
    const [errorMsg, setErrorMsg] = useState<string[]>([]);
    const [newTerminal, setNewTerminal] = useState<Terminal>();
    const [selectedId, setSelectedId] = useState<number | null>(() => peekPendingSelectedId("terminals"));
    const [rowClickCount, setRowClickCount] = useState({});

    const { keycloak, initialized } = useKeycloak();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const dummyTerminal: Terminal = {
        poi: "",
        hardwareId: "",
        siteIdNumber: 0,
        siteId: null,
        isConnected: false,
        siteDescription: "",
        maxVmcs: 1,
        vmcIds: [],
        ecrState: "",
        vasState: "",
        vasResponseState: "",
        description: "",
        id: 0,
        isVasMdbTerminal: "False",
        isActive: "False",
        propertyNames: undefined,
        note: "",
        status: ""
    };

    const setData = (data: Terminal) => {
        localStorage.setItem('Terminal', JSON.stringify(data));
    }

    const delay = async (ms: number) => {
        await new Promise(resolve => setTimeout(resolve, ms));
    }

    const csvHeaders = [
        { label: 'POI', key: 'poi' },
        { label: 'Terminal', key: 'description' },
        { label: t('csv.created'), key: 'created' },
        { label: 'Site ID', key: 'siteId' },
        { label: t('csv.txCount'), key: 'txCount' },
        { label: t('csv.lastTransaction'), key: 'txDate' },
    ];

    let timer: NodeJS.Timeout | null = null;

    const submitSearch = () => {
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const handleChange = () => {
        if (timer) {
            clearTimeout(timer)
        }
        timer = setTimeout(submitSearch, 2000);
        setCurrentPage(0);        
    }

    const handlePaging = async (e: number) => {
        errorMsg.length = 0;

        do {
            await delay(1000);
        } while (isRefreshing);
        
        setCurrentPage(e);

        const start = ((e + 1) * 25) - 25;
        const end = ((e + 1) * 25);
        const rangeString = `${start}-${end}`
        setPagingString(rangeString);

        document.querySelector('#refresh')?.classList.remove("d-none")
        setIsRefreshing(true);

        getTerminals(keycloak.token || '', rangeString, { search: formData, Online: filterData, sites: siteFilter.join(), sitesQuery: siteFilterQuery }, searchData)
            .then((result) => {
                setTerminals(result.body);
                let terminal = dummyTerminal;
                terminal.propertyNames = result?.body[0]?.propertyNames;
 
                if (!terminal.propertyNames) {
                    getPropertyInfo(keycloak.token || '').then((result) => {
                        terminal.propertyNames = result;
                    })
                }

                setNewTerminal(terminal);
                var totalCount = result.header.split('/')[1];
                setTerminalCount(totalCount);
                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
            })
            .catch((error: ApiError) => {
                try {
                    document.querySelector('.errorMsg')?.classList.remove("d-none");
                    Object.entries<Record<string, any>>(JSON.parse(error.body)["errors"]).forEach((k) => { k[1].forEach((msg: string) => { errorMsg.push(msg); setErrorMsg(errorMsg); }) });
                }
                catch (ex) {
                    errorMsg.push("Error handling response");
                    setErrorMsg(errorMsg);
                }

                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
            });
    }

    const handleCsvExport = (): Promise<ApiResult> => {
        const rangeString = "0";
        const result = getTxCount(keycloak.token || '', rangeString, { search: formData }, searchData);
        return result;
    }

    const handleFilter = (e: string): void => {
        setTerminals([]);
        setFilterData(e);
        setCurrentPage(0);
    }

    const handleSearch = (e: string): void => {
        setTerminals([]);
        searchData["search"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumn1 = (e: string): void => {
        setTerminals([]);
        searchData["description"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumn2 = (e: string): void => {
        setTerminals([]);
        searchData["locationDescription"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumn3 = (e: string): void => {
        setTerminals([]);
        searchData["lastMessageReceivedDateTime"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleRowClick = (tx) => {
        if (window.matchMedia('(pointer: coarse)').matches) {
            setRowClickCount({ [tx.id]: 1 });
        }
        
        setRowClickCount((prev) => {
            const navContext: NavContext = {
                from: 'lists/terminals',
                id: null
            };

            resetNavContext(navContext);

            const newCount = (prev[tx.id] || 0) + 1;
            if (newCount === 1) {
            setSelectedId(tx.id);
            return { ...prev, [tx.id]: newCount };
            } else if (newCount === 2) {
            setData(tx);
            navigate("/dashboard/details/terminal");
            setSelectedId(tx.id);
            return { ...prev, [tx.id]: 0 };
            }
            return prev;
        });
    };

    const handleRefreshButton = (): void => {
        setTerminals([]);
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>): boolean => {
        if (event.key === 'Enter') {
            event.preventDefault();
            return false;
        }
        return true;
    }

    const init = () => {
        document.querySelector('.errorMsg')?.classList.add("d-none");
        handlePaging(currentPage ?? 0);
        initSearchFields();
    }; 

    const initSearchFields = () => {
        Object.keys(searchData).map(x => {
            const search: HTMLInputElement | null = document.querySelector(`.${x}`) as HTMLInputElement;
            if (search) {
                search.value = searchData[x];
            }
        })
    }

    const onResize = () => {
        var e = document.querySelector(".vertical-scrollable") as HTMLElement;
        var h = window.innerHeight;
        e.style.height = (h - 284) + "px";
        e.style.maxHeight = (h - 280) + "px";
        setTableBodyHeight(h - 284);        
    };

    useEffect(() => {
        window.addEventListener("resize", onResize);
        onResize();
        if (terminals?.length === 0) {
            init();
        }
        clearPendingSelectedId("terminals");
        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, []);

    useEffect(() => {
        if (terminals.length > 0) return;
        init();
    }, [formData, filterData]);    

    //const handleRequestCtmp = useCallback((id: number) => {
    //    requestCtmp(keycloak.token || '', id).catch((err) => setError(err));
    //}, []);


    return (
        <>
            <Row className="">                  
                <Col className="">
                    <Card className="rounded">                        
                        <Card.Body className="">
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="">
                                <Col sm="12" className="">    
                                    <h4 className="mb-2  qvend-title">{t("terminals")}</h4>                                    
                                </Col>
                            </Row>                            
                            <Row style={{ fontSize: '11px' }}>
                                <Col md={3} xs={5} style={{ paddingRight: 0 }}>
                                    <div className="bd-example">
                                        <div>
                                            <SearchInput className="form-control search" type="search" placeholder={!searchData["search"] ? t("search") : undefined } onChange={handleSearch} value={searchData.search ?? ""} />
                                        </div>                                            
                                    </div>                                    
                                </Col>
                                <Col md={3} xs={1}>
                                    <button type="button" className="btn btn-qvend" style={{ fontSize: '11px', padding: '0.5rem 0.3rem' }}>...</button>                                    
                                </Col>
                                <Col className="text-end">
                                    {hasRole(newTerminal, "siteId", keycloak) === true && hasSites(newTerminal) ? (<Link to="/dashboard/details/terminal" className="btn btn-qvend" style={{ fontSize: '11px', padding: '0.5rem 0.5rem', margin: '5px' }} onClick={() => {
                                        let siteIdValue: string = "";
                                        if (newTerminal && newTerminal!.propertyNames && newTerminal!.propertyNames["siteId"] && newTerminal!.propertyNames["siteId"]["predefinedValues"]) {
                                            let values = newTerminal!.propertyNames["siteId"]["predefinedValues"];
                                            if (values.length === 1) {
                                                siteIdValue = values[0];
                                            }
                                        }
                                        newTerminal && (newTerminal.siteId = siteIdValue ? siteIdValue : (siteFilter.length === 1 ? siteFilter.pop()!.toString() : "0")); setData(newTerminal!);
                                        const navContext: NavContext = {
                                            from: 'lists/terminals',
                                            id: null
                                        };
                                        resetNavContext(navContext);
                                    }} key={0}>+</Link>) : (
                                            <a className="btn btn-qvend" style={{ pointerEvents: 'none', color: 'gray', backgroundColor: 'lightgray', cursor: 'default', fontSize: '11px', padding: '0.5rem 0.5rem', margin: '5px' }}
                                            onClick={(e) => e.preventDefault()}
                                        >+</a>
                                    ) }                                    
                                    {keycloak && keycloak.hasResourceRole("r-admin") && (<CsvExport onGetData={handleCsvExport} headers={csvHeaders} separator={";"} filename={"terminals.csv"} className="btn btn-qvend" style={{ fontSize: '11px' }} />)}
                                    {(!keycloak || !keycloak.hasResourceRole("r-admin")) && (<button type="button" className="btn btn-qvend" style={{ fontSize: '11px' }}>Download</button>)}
                                </Col>
                            </Row>
                            <form className="" onKeyDown={handleKeyDown}>
                                <Row>
                                    <Col md={12} xs={10} style={{ paddingRight: 0 }}>
                                        <div>
                                            <fieldset className="form-control-sm" style={{ fontSize: '11px' }}>
                                                <Form.Check className="form-check-inline">
                                                    <FormCheck.Input onChange={() =>
                                                        handleFilter("")
                                                    } type="radio" name="radios" className="form-check-input" id="radio1" defaultChecked={ filterData === "" } />
                                                    <FormCheck.Label className="form-check-label" htmlFor="radio1">{t('terminals.filter.all')}</FormCheck.Label>
                                                </Form.Check>
                                                <Form.Check className="form-check-inline">
                                                    <FormCheck.Input onChange={() =>
                                                        handleFilter("true")
                                                    } type="radio" name="radios" className="form-check-input" id="radio2" defaultChecked={filterData === "true"}/>
                                                    <FormCheck.Label className="form-check-label" htmlFor="radio2">{t('terminals.filter.online')}</FormCheck.Label>
                                                </Form.Check>
                                                <Form.Check className="form-check-inline">
                                                    <FormCheck.Input onChange={() =>
                                                        handleFilter("false")
                                                    } type="radio" name="radios" className="form-check-input" id="radio3" defaultChecked={filterData === "false"}/>
                                                    <FormCheck.Label className="form-check-label" htmlFor="radio3">{t('terminals.filter.offline')}</FormCheck.Label>
                                                </Form.Check>
                                                {((siteFilter && siteFilter.length > 0) || (siteFilterQuery && siteFilterQuery.length > 2)) ? <span className="text-end">
                                                    <Badge pill bg="dark" className="p-1 " onClick={() => { setSiteFilter([]); setSiteFilterQuery(""); setTerminals([]); setCurrentPage(0); formData === "" ? setFormData(" ") : setFormData(""); window.localStorage.removeItem("searchDataSites") }}><CloseButton variant="white" /> Sites</Badge></span> : null}
                                            </fieldset>
                                        </div>
                                    </Col>
                                </Row>
                            </form>
                            <Row className="">
                                <Col sm="12" xs="12" className="mt-2">
                                    <RefreshPull isRefreshing={isRefreshing} onSetPage={handlePaging} />
                                    <div className="table-responsive-lg" style={{ overflowX: 'hidden' }}>
                                        <div>
                                            <Row className="qvend-table-font" style={{ background: "#2b2e83", color: "#FFFFFF", overflowY: "scroll"  }}>
                                                <Col md={5} xs={6} className="qvend-table table-column-first">{t('terminal.terminal')}</Col>
                                                <Col md={4} xs={6} className="qvend-table">{t('terminal.location')}</Col>
                                                <Col md={3} xs={0} className="qvend-table d-none d-md-block">{t('terminal.lastSeen')}</Col>                                                
                                            </Row>
                                        </div>
                                        <div className="vertical-scrollable" style={{ overscrollBehaviorY: 'contain' }}>
                                            <Row className={("qvend-table-font")} style={{ background: "#cfd5ea", color: "black" }}>
                                                <Col md={5} xs={6} className="qvend-table search-in-row-first"><SearchInput className="form-control description" type="search" onChange={handleSearchColumn1} value={searchData.description ?? ""} /></Col>
                                                <Col md={4} xs={6} className="qvend-table search-in-row-padding"><SearchInput className="form-control locationDescription" type="search" onChange={handleSearchColumn2} value={searchData.locationDescription ?? ""} /></Col>
                                                <Col md={3} xs={0} className="qvend-table search-in-row-padding d-none d-md-block"><SearchInput className="form-control lastMessageReceivedDateTime" type="search" onChange={handleSearchColumn3} value={searchData.lastMessageReceivedDateTime ?? ""} /></Col>
                                            </Row>
                                            <Row className="d-none errorMsg">
                                                <Col>
                                                    {
                                                        errorMsg.map((item, index) => {
                                                            return <Alert key={index} variant="warning" className="alert" role="alert">
                                                                <span>{item}</span>
                                                            </Alert>;
                                                        })
                                                    }
                                                </Col>
                                            </Row>
                                            {terminals.map((term: Terminal, index) => (
                                                <Link to="#" onClick={() => handleRowClick(term)} key={term.id}>
                                                    <TerminalItem
                                                        isSelected={selectedId === term.id}
                                                        key={term.id}
                                                        terminal={term}                                                       
                                                        rowColor={selectedId === term.id ? "gray" :index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                    />
                                                </Link>
                                            ))}
                                            {                                                
                                                //Array.from({ ...dummyTerminal, length: (tableBodyHeight / 16.5) - terminals.length } as unknown as Terminal[]).map((terminal: Terminal, index) => (
                                                //    <TerminalItem
                                                //        key={index + terminals.length}
                                                //        terminal={dummyTerminal}
                                                //        rowColor={index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                //    />
                                                //))
                                            }
                                        </div>
                                    </div>
                                </Col>                              
                            </Row>
                            <ListPagination
                                articlesCount={terminalCount}
                                currentPage={currentPage}
                                onSetPage={handlePaging}
                                onRefresh={handleRefreshButton} />
                        </Card.Body>
                    </Card>
                </Col>                                    
            </Row>
        </>
    )
}

function hasRole(terminal: any, key: string, keycloak: any) {
    if (!(terminal && terminal!.propertyNames)) {
        return false;
    }

    const roleNames = terminal["propertyNames"][key].roleNames;
    let result: boolean = false;
    var returnValue = roleNames === null || keycloak && roleNames.map((role: string) => { result = result || keycloak.hasResourceRole(role) }) && result;
    return returnValue;
}

function hasSites(terminal: any) {
    if (!(terminal && terminal!.propertyNames && terminal!.propertyNames["siteId"]["predefinedValues"])) {
        return false;
    }

    const siteIds = Array.from(terminal!.propertyNames["siteId"]["predefinedValues"]);

    if (siteIds.length === 0) {
        return false;
    }

    return true;
}

export default Terminals
